import cn from 'classnames'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { selectTranslationMode } from 'cng-common/features/translations/selectors'
import { Dropdown, DropdownDynamic } from 'core-components/library/dropdown'
import { Tooltip } from 'core-components/library/tooltip'

import { ValidationType } from '../../configs'
import { useFormField } from '../../hooks/useFormField'
import { ReadOnlyField } from '../readOnlyField'

import { getDropdownValueByKeyField } from './helpers'
import { IFormDropdownProps } from './models'

import styles from './FormDropdown.module.scss'

const typedMemo: <T>(c: T) => T = memo

export const FormDropdown = typedMemo(
  <T,>({
    field,
    formKey,
    label = '',
    defaultValue,
    source,
    caption,
    classes,
    onChange,
    onValidate,
    isStatic,
    isDisabled,
    isRequired,
    isReadOnly,
    isVisible = true,
    keyField,
    renderFunction,
    validationType = ValidationType.ONCHANGE,
    hasHierarchy,
    placeholder,
    ...props
  }: IFormDropdownProps<T>) => {
    const { handleChange, value, validationMessage, isModified } = useFormField({
      field,
      formKey,
      defaultValue,
      onValidate,
      isDisabled,
      isRequired,
      isReadOnly,
      isVisible,
      validationType,
    })

    // Инициализируем пустой строкой, так как Dropdown ожидает string
    const [dropdownValue, setDropdownValue] = useState<string>('')

    const translationMode = useSelector(selectTranslationMode)
    const isTranslationMode = translationMode === 'edit'
    const t = useSafetyTranslation()

    const translatedPlaceholder = placeholder ? t(placeholder) : undefined

    const renderFunctionWrapper = useMemo(
      () => (renderFunction ? (item: unknown) => renderFunction(item as T) : undefined),
      [renderFunction]
    )

    // Приводим к строке безопасно: null/undefined превратятся в ""
    const safeDisplayValue = useMemo(() => dropdownValue?.toString() ?? '', [dropdownValue])

    const handleOnChange = useCallback(
      async (newValue: string, item: T) => {
        const handler = (val: string, it: T) => {
          onChange && onChange(val, it)
          handleChange(val)
          setDropdownValue(val ?? '')
        }

        if (keyField && item) {
          handler(String(item[keyField] ?? ''), item)
        } else {
          handler(newValue ?? '', item)
        }
      },
      [handleChange, keyField, onChange]
    )

    const handleOnCreate = useCallback(
      async (newValue: string) => {
        const val = newValue ?? ''
        handleChange(val)
        setDropdownValue(val)
      },
      [handleChange]
    )

    const DropdownComponent = isStatic ? Dropdown : DropdownDynamic

    useEffect(() => {
      let isMounted = true

      if (keyField) {
        getDropdownValueByKeyField({
          caption,
          value,
          source,
          keyField,
        }).then((res) => {
          if (isMounted) {
            setDropdownValue(res ?? '')
          }
        })
      } else {
        setDropdownValue(value ?? '')
      }

      return () => {
        isMounted = false
      }
    }, [caption, formKey, keyField, source, value])

    if (!isVisible) {
      return null
    }

    if (isReadOnly) {
      // Здесь тоже используем safeDisplayValue или value с защитой
      return <ReadOnlyField label={label} value={keyField ? safeDisplayValue : (value ?? '')} />
    }

    return (
      <div className={styles.validationContainer}>
        <Tooltip disabled={!validationMessage} tipContent={() => <>{validationMessage}</>}>
          <RenderWhen condition={isTranslationMode && Boolean(label)}>
            <Translation text={label} span />
          </RenderWhen>
          <DropdownComponent
            {...props}
            classes={{
              ...classes,
              controlClasses: {
                ...classes?.controlClasses,
                root: cn(classes?.controlClasses?.root, {
                  [styles.invalid]: validationMessage,
                  [styles.modified]: isModified,
                  [styles.required]: isRequired && Boolean(label),
                }),
              },
              inputClasses: {
                ...classes?.inputClasses,
                root: cn(classes?.inputClasses?.root, {
                  [styles.invalidInput]: validationMessage,
                  [styles.modified]: isModified,
                }),
              },
            }}
            label={label ? t(label) : undefined}
            renderFunction={renderFunctionWrapper}
            // ГАРАНТИЯ: теперь здесь всегда строка, даже если в стейте null
            value={safeDisplayValue}
            onChange={handleOnChange}
            onCreate={handleOnCreate}
            source={source}
            caption={caption}
            disabled={isDisabled}
            hasHierarchy={hasHierarchy}
            placeholder={translatedPlaceholder}
          />
        </Tooltip>
      </div>
    )
  }
)
